import { vec2, vec3 } from "gl-matrix"
import { Texture } from "./backends/webgl/texture"
import { Renderer } from "./pipeline/renderer"
import { TexturesPool } from "./utils/textures-pool"
import { copyFrom } from "./utils/texture-copy"
import { BrushStroke } from "./utils/brushing"
import { clamp } from "./utils/math"
import { fitToSize } from "./utils/resize-image"
import { throttled } from "@/utils/throttle"
import { Rectangle } from "./types"
import { rectClamp, rectPadding } from "@/utils/math"
import { MaskPainter } from "./utils/mask-painter"

const getRectDimension = (a: Rectangle, width: number, height: number): vec2 => {
	return [Math.round(a.width * width), Math.round(a.height * height)]
}

type ExchangeData = {
	patchSize: vec2
	searchDims: vec2
	searchLoc: Rectangle
	inputSize: vec2
	maskData: Uint8Array
	targetData: Uint8Array
	inputData: Uint8Array
	inputGradData: Uint8Array
}

type RenderTask = {
	targetLocation: Rectangle
	strokes: BrushStroke[]
	callback: (value: ExchangeData | undefined) => void
}

type SearchData = {
	input: Uint8Array
	inputGrad: Uint8Array
	mask: Uint8Array
	target: Uint8Array
	boundaryIndices: vec3[]
	innerIndices: vec3[]
	inputDims: vec2
}

export class HealingSourceDetector {
	private renderer: Renderer
	private texturesPool: TexturesPool // rgba8
	private maskPainter: MaskPainter
	private waitingSync = false
	private renderTasks: RenderTask[] = []
	private static MaxSize: vec2 = [2000, 2000]

	constructor(renderer: Renderer) {
		this.renderer = renderer
		this.texturesPool = new TexturesPool(renderer.backend, "rgba8unorm")
		this.maskPainter = new MaskPainter(this.renderer, true)
	}

	private copyFrom(options: { source: Texture; srcLocation: Rectangle; destination: Texture }) {
		copyFrom({ ...options, renderer: this.renderer })
	}

	private getGradientTexture(inputTexture: Texture, texturesPool: TexturesPool) {
		const outputTexture = texturesPool.withdraw(
			inputTexture.width,
			inputTexture.height,
			"healingSourceDetector.gradientTexture",
		)
		this.renderer.setRenderTarget(outputTexture)
		inputTexture.bind(0)
		this.renderer.shaders.healingSobel.render({
			inputTexture: 0,
		})
		return outputTexture
	}

	private getOverlapPercent(xy1: vec2, xy2: vec2, size: vec2) {
		const [xMin1, xMax1, yMin1, yMax1] = [xy1[0], xy1[0] + size[0], xy1[1], xy1[1] + size[1]]
		const [xMin2, xMax2, yMin2, yMax2] = [xy2[0], xy2[0] + size[0], xy2[1], xy2[1] + size[1]]

		// Calculate overlap width and height
		const width = Math.min(xMax1, xMax2) - Math.max(xMin1, xMin2)
		const height = Math.min(yMax1, yMax2) - Math.max(yMin1, yMin2)

		return Math.max(0, width * height) / (size[0] * size[1])
	}

	private computeSourceError(options: { data: SearchData; startCoords: vec2; weights?: vec2 }) {
		const { data, startCoords } = options
		const [xStart, yStart] = startCoords as [number, number]
		const [inputW, _] = data.inputDims as [number, number]
		const weights = options.weights ?? [0.5, 0.5]

		let diffBoundary = 0
		if (weights[0] > 0) {
			for (const index of data.boundaryIndices) {
				const [i, x, y] = index as [number, number, number]
				const inputIndex = ((yStart + y) * inputW + xStart + x) * 4
				const diffR = (data.target[i] || 0) - (data.input[inputIndex] || 0)
				const diffG = (data.target[i + 1] || 0) - (data.input[inputIndex + 1] || 0)
				const diffB = (data.target[i + 2] || 0) - (data.input[inputIndex + 2] || 0)
				const minDiff = Math.min(diffR, diffG, diffB)
				const maxDiff = Math.max(diffR, diffG, diffB)
				const colorDiff = (maxDiff - minDiff) / 2
				diffBoundary += Math.abs(diffR) + Math.abs(diffG) + Math.abs(diffB) + colorDiff
			}
			diffBoundary /= data.boundaryIndices.length * 3 * 8
		}

		let gradError = 0
		if (weights[1] > 0) {
			let aveGrad = 0
			let edgeNumber = 0
			for (const index of data.innerIndices) {
				const [_, x, y] = index as [number, number, number]
				const inputIndex = ((yStart + y) * inputW + xStart + x) * 4
				const grad = data.inputGrad[inputIndex] as number
				aveGrad += grad
				if (grad > 5) edgeNumber++
			}
			aveGrad /= data.innerIndices.length
			const edgePercentError = edgeNumber / inputW
			gradError = aveGrad + clamp(edgePercentError, 0, 2)
		}

		return diffBoundary * weights[0] + gradError * weights[1]
	}

	private getTargetBoundaryGradient(data: SearchData, targetCoord: vec2) {
		const [xStart, yStart] = targetCoord as [number, number]
		const inputW = data.inputDims[0]

		let aveGrad = 0
		let edgeNumber = 0
		for (const index of data.boundaryIndices) {
			const [_, x, y] = index as [number, number, number]
			const inputIndex = ((yStart + y) * inputW + xStart + x) * 4
			const grad = data.inputGrad[inputIndex] as number
			aveGrad += grad
			if (grad > 5) edgeNumber++
		}
		aveGrad /= data.boundaryIndices.length
		const edgePercentError = edgeNumber / data.boundaryIndices.length

		return aveGrad + clamp(edgePercentError, 0, 2)
	}

	private getScale(width: number, height: number) {
		const maxPatchDim = 200 // to speed up
		const scale = Math.min(maxPatchDim / Math.max(width, height), 1)
		return scale
	}

	private findOptimalSourceLoc_GPU(
		target: Texture,
		targetLocation: Rectangle, // padded loc
		mask: Texture,
		input: Texture,
		scale: number,
	): ExchangeData {
		// Search region:
		const searchR = 4
		const searchLoc = rectClamp(
			rectPadding(
				targetLocation,
				targetLocation.width * searchR,
				targetLocation.height * searchR,
			),
			0,
			1,
		)
		// Scale down all textures and convert to uint8norm for reading
		const scaledImgSize: vec2 = [
			Math.round(input.width * scale),
			Math.round(input.height * scale),
		]

		const searchDims = getRectDimension(searchLoc, scaledImgSize[0], scaledImgSize[1])
		const searchInput = this.texturesPool.withdraw(
			searchDims[0],
			searchDims[1],
			"healingSourceDetector.searchInputTexture",
		)
		this.copyFrom({ source: input, srcLocation: searchLoc, destination: searchInput })
		const inputGradient = this.getGradientTexture(searchInput, this.texturesPool)

		const patchSize: vec2 = [target.width, target.height]

		// Read data from textures, target/mask are already scaled
		const maskData = new Uint8Array(mask.width * mask.height * 4)
		mask.read({ bufferView: maskData })
		const targetData = new Uint8Array(target.width * target.height * 4)
		target.read({ bufferView: targetData })
		const inputData = new Uint8Array(searchInput.width * searchInput.height * 4)
		searchInput.read({ bufferView: inputData })
		const inputGradData = new Uint8Array(inputGradient.width * inputGradient.height * 4)
		inputGradient.read({ bufferView: inputGradData })

		this.texturesPool.deposit(searchInput)
		this.texturesPool.deposit(inputGradient)

		return {
			patchSize,
			searchDims,
			searchLoc,
			inputSize: scaledImgSize,
			maskData,
			targetData,
			inputData,
			inputGradData,
		}
	}

	private findOptimalSourceLoc_CPU(
		targetLocation: Rectangle,
		parameters: ExchangeData,
	): Rectangle | undefined {
		//this.renderer.beginTimer("healing search cpu")

		const {
			patchSize,
			searchDims,
			searchLoc,
			inputSize,
			maskData,
			targetData,
			inputData,
			inputGradData,
		} = parameters

		// Initialize indices for faster iteration
		const boundaryIndices: vec3[] = []
		const innerIndices: vec3[] = []
		const [patchW, patchH] = patchSize as [number, number]
		for (let y = 0; y < patchH; y++) {
			for (let x = 0; x < patchW; x++) {
				const i = (y * patchW + x) * 4
				if (<number>maskData[i] < 1) {
					const neighbor1 = maskData[(y * patchW + x + 1) * 4] || 0
					const neighbor2 = maskData[(y * patchW + x - 1) * 4] || 0
					const neighbor3 = maskData[((y + 1) * patchW + x) * 4] || 0
					const neighbor4 = maskData[((y - 1) * patchW + x) * 4] || 0

					if (neighbor1 > 0 || neighbor2 > 0 || neighbor3 > 0 || neighbor4 > 0) {
						boundaryIndices.push([i, x, y])
					}
				} else {
					innerIndices.push([i, x, y])
				}
			}
		}

		// Search best source: grid search coarse -> fine
		const data: SearchData = {
			input: inputData,
			inputGrad: inputGradData,
			mask: maskData,
			target: targetData,
			boundaryIndices,
			innerIndices,
			inputDims: searchDims,
		}
		const targetX = Math.round((targetLocation.x - searchLoc.x) * inputSize[0])
		const targetY = Math.round((targetLocation.y - searchLoc.y) * inputSize[1])
		const targetCoord: vec2 = [targetX, targetY]

		// weights for boundaryDiff and aveInnerGrad
		const weights: vec2 = [0.5, 0.5]
		const targetGradient = this.getTargetBoundaryGradient(data, targetCoord)
		// if target has large gradient, then we should use a lower gradient weight
		const deltaGradW = -clamp((targetGradient - 0.6) / 4, 0, 0.47)
		weights[0] -= deltaGradW
		weights[1] += deltaGradW

		// 1. do a coarse grid search to find top 3 candidates
		const maxX = searchDims[0] - patchSize[0]
		const maxY = searchDims[1] - patchSize[1]
		const coarseGridNumber = 21
		const { coords, errors } = this.gridSearch({
			data,
			rangeX: [0, maxX],
			rangeY: [0, maxY],
			targetCoord,
			patchSize,
			gridNumber: coarseGridNumber,
			weights,
			returnTopN: 3,
			overlapThresh: 0.2,
			hitTarget: true,
		})

		// 2. do a finer grid search around the top candidate regions
		let minError = errors[0] ?? 1000
		let finalCoord = coords[0] ?? targetCoord
		const startCoords: vec2[] = []
		for (let i = 0; i < coords.length; i++) {
			if (<number>errors[i] <= minError * 1.8) {
				startCoords.push(<vec2>coords[i])
			}
		}

		const fineCoords: vec2[] = []
		let lastStepX = Math.floor((maxX / coarseGridNumber) * 0.95)
		let lastStepY = Math.floor((maxY / coarseGridNumber) * 0.95)
		for (const startCoord of startCoords) {
			const [x, y] = startCoord
			const rangeX: vec2 = [Math.max(x - lastStepX, 0), Math.min(x + lastStepX, maxX)]
			const rangeY: vec2 = [Math.max(y - lastStepY, 0), Math.min(y + lastStepY, maxY)]
			const { coords, errors } = this.gridSearch({
				data,
				rangeX,
				rangeY,
				targetCoord,
				patchSize,
				gridNumber: 10,
				weights,
				returnTopN: 1,
				overlapThresh: 0.2,
			})
			const coords0 = coords[0] as vec2
			if (errors[0] != undefined && errors[0] < minError) {
				minError = errors[0]
				finalCoord = coords0
			}
			fineCoords.push(coords0)
		}

		// 3. do a finest gird search using around top candidates
		lastStepX = Math.round(lastStepX / 5.5)
		lastStepY = Math.round(lastStepY / 5.5)
		const finalGridNum = Math.min(5, lastStepX, lastStepY)
		for (const startCoord of fineCoords) {
			const [x, y] = startCoord as [number, number]
			const rangeX: vec2 = [Math.max(x - lastStepX, 0), Math.min(x + lastStepX, maxX)]
			const rangeY: vec2 = [Math.max(y - lastStepY, 0), Math.min(y + lastStepY, maxY)]
			const { coords, errors } = this.gridSearch({
				data,
				rangeX,
				rangeY,
				targetCoord,
				patchSize,
				gridNumber: finalGridNum,
				weights,
				returnTopN: 1,
				overlapThresh: 0.2,
				fastTopN: 4,
			})
			if (errors[0] != undefined && errors[0] < minError) {
				minError = errors[0]
				finalCoord = coords[0] as vec2
			}
		}

		//this.renderer.endTimer("healing search cpu")

		const xInSearchRegion = finalCoord[0] / searchDims[0]
		const yInSearchRegion = finalCoord[1] / searchDims[1]
		const xMin = searchLoc.x + xInSearchRegion * searchLoc.width
		const yMin = searchLoc.y + yInSearchRegion * searchLoc.height

		return { x: xMin, y: yMin, width: targetLocation.width, height: targetLocation.height }
	}

	private gridSearch(options: {
		data: SearchData
		rangeX: vec2
		rangeY: vec2
		targetCoord: vec2
		patchSize: vec2
		gridNumber: number
		weights: vec2
		returnTopN: number
		overlapThresh: number
		hitTarget?: boolean
		fastTopN?: number
	}) {
		const { data, weights } = options

		// Step 1: do faster grid search using boundaryError (boundary pixel difference) only
		const { topCoords, topBoundaryErrors } = this.gridSearchByBoundary(options)

		// Step 2: take top N of fast grid search results and compute full error, find the best ones
		const fullErrors: number[] = []
		const finalCoords: vec2[] = []
		for (let i = 0; i < topBoundaryErrors.length; i++) {
			const topBoundaryErrorI = topBoundaryErrors[i] as number
			const coord = topCoords[i] as vec2
			let gradError = this.computeSourceError({
				data,
				startCoords: coord,
				weights: [0, 1], // gradError only
			})
			const boundaryError = topBoundaryErrorI
			// When boundaryError is very low, we shouldn't rely on gradError too much (allow inner patterns)
			gradError = clamp(
				gradError * clamp(boundaryError, 0.25, 1),
				0,
				boundaryError * (2.5 + weights[1] * 3),
			)
			const error = topBoundaryErrorI * weights[0] + gradError * weights[1]
			fullErrors.push(error)
			finalCoords.push(coord)
		}

		return this.sortedByError(finalCoords, fullErrors, options.returnTopN)
	}

	private gridSearchByBoundary(options: {
		data: SearchData
		rangeX: vec2
		rangeY: vec2
		targetCoord: vec2
		patchSize: vec2
		gridNumber: number
		weights?: vec2
		returnTopN: number
		overlapThresh: number
		hitTarget?: boolean
		fastTopN?: number
	}) {
		const { data, rangeX, rangeY, targetCoord, patchSize, gridNumber, overlapThresh } = options
		const topN = options.fastTopN ?? 10
		const [minX, maxX] = rangeX
		const [minY, maxY] = rangeY
		const boundaryErrorThresh = 1.0

		const stepX = Math.max(1, Math.floor((maxX - minX) / gridNumber))
		const stepY = Math.max(1, Math.floor((maxY - minY) / gridNumber))
		let startX = minX
		let startY = minY
		if (options.hitTarget === true) {
			const i = Math.floor((targetCoord[0] - minX) / stepX)
			startX = targetCoord[0] - i * stepX
			const j = Math.floor((targetCoord[1] - minY) / stepY)
			startY = targetCoord[1] - j * stepY
		}

		// First search on original step
		let { coords, errors } = this.computeTopSortedBoundaryErrors({
			xRange: [startX, maxX],
			yRange: [startY, maxY],
			stepX,
			stepY,
			targetCoord,
			patchSize,
			data,
			overlapThresh,
			topN,
		})

		const halfStepX = Math.round(stepX / 2)
		const halfStepY = Math.round(stepY / 2)

		// If didn't find a good spot, effectively reduce the search step by half by moving the start search position
		let bestBoundaryError = errors[0] || 0
		if (bestBoundaryError > boundaryErrorThresh && gridNumber > 12) {
			const result2 = this.computeTopSortedBoundaryErrors({
				xRange: [startX + halfStepX, maxX],
				yRange: [startY + halfStepY, maxY],
				stepX,
				stepY,
				targetCoord,
				patchSize,
				data,
				overlapThresh,
				topN,
			})

			bestBoundaryError = result2.errors[0] || 0

			coords = coords.concat(result2.coords)
			errors = errors.concat(result2.errors)
		}

		// If didn't find a good spot, further change start search position
		if (bestBoundaryError > boundaryErrorThresh && gridNumber > 12) {
			const result3 = this.computeTopSortedBoundaryErrors({
				xRange: [startX, maxX],
				yRange: [startY + halfStepY, maxY],
				stepX,
				stepY,
				targetCoord,
				patchSize,
				data,
				overlapThresh,
				topN,
			})

			const result4 = this.computeTopSortedBoundaryErrors({
				xRange: [startX + halfStepX, maxX],
				yRange: [startY, maxY],
				stepX,
				stepY,
				targetCoord,
				patchSize,
				data,
				overlapThresh,
				topN,
			})

			coords = coords.concat(result3.coords).concat(result4.coords)
			errors = errors.concat(result3.errors).concat(result4.errors)
		}

		const result = this.sortedByError(coords, errors, topN)
		return {
			topCoords: result.coords,
			topBoundaryErrors: result.errors,
		}
	}

	private computeTopSortedBoundaryErrors(input: {
		xRange: vec2
		yRange: vec2
		stepX: number
		stepY: number
		targetCoord: vec2
		patchSize: vec2
		data: SearchData
		overlapThresh: number
		topN: number
	}) {
		const { xRange, yRange, stepX, stepY, targetCoord, patchSize, data, overlapThresh, topN } =
			input
		const trialCoords: vec2[] = []
		const boundaryErrors: number[] = []
		for (let x = xRange[0]; x <= xRange[1]; x += stepX) {
			for (let y = yRange[0]; y <= yRange[1]; y += stepY) {
				const coord: vec2 = [x, y]
				const overlap = this.getOverlapPercent(coord, targetCoord, patchSize)
				if (overlap > overlapThresh) continue

				trialCoords.push(coord)
				const error = this.computeSourceError({
					data,
					startCoords: coord,
					weights: [1, 0], // fast search with boundary diff only
				})
				boundaryErrors.push(error)
			}
		}

		return this.sortedByError(trialCoords, boundaryErrors, topN)
	}

	private sortedByError(coords: vec2[], errors: number[], topN: number) {
		// index sort
		const indices = errors.map((_, index) => index)
		indices.sort((a, b) => <number>errors[a] - <number>errors[b])
		const topIndices = indices.slice(0, topN)

		return {
			coords: topIndices.map((val) => coords[val] as vec2),
			errors: topIndices.map((val) => errors[val] as number),
		}
	}

	private findSource_GPU(
		targetLocation: Rectangle,
		strokes: BrushStroke[],
	): ExchangeData | undefined {
		const renderer = this.renderer
		const photo = renderer.photo
		const { width, height } = photo.texture

		//this.renderer.beginTimer("healing search gpu")

		// Get input texture:
		// downsample size
		const scaledOriginalSize = fitToSize(
			photo.imageState.originalImageSize,
			HealingSourceDetector.MaxSize,
		)

		// Render stable healing space at source-detection resolution.
		const rwBuffers = renderer.dualBuffers
		rwBuffers.readBuffer.updateSize(scaledOriginalSize[0], scaledOriginalSize[1])
		renderer.transformPass.render(photo.texture, rwBuffers.readBuffer, { isForHealing: true })

		const [currPatchW, currPatchH] = getRectDimension(
			targetLocation,
			scaledOriginalSize[0],
			scaledOriginalSize[1],
		)
		// Further scale down respect to patch size
		const scale = this.getScale(currPatchW, currPatchH)

		// scale target & mask
		const patchSize: vec2 = [Math.round(currPatchW * scale), Math.round(currPatchH * scale)]
		const mask = this.texturesPool.withdraw(
			patchSize[0],
			patchSize[1],
			"healingSourceDetector.maskTexture",
		)

		this.maskPainter.drawMask(strokes, mask, {
			isAdditive: false,
			rect: targetLocation,
			aspectRatio: height / width,
		})

		// copy target
		const target = this.texturesPool.withdraw(
			patchSize[0],
			patchSize[1],
			"healingSourceDetector.targetTexture",
		)
		this.copyFrom({
			source: rwBuffers.readBuffer,
			srcLocation: targetLocation,
			destination: target,
		})

		const exchangeData = this.findOptimalSourceLoc_GPU(
			target,
			targetLocation,
			mask,
			rwBuffers.readBuffer,
			scale,
		)

		this.texturesPool.deposit(target)
		this.texturesPool.deposit(mask)

		//this.renderer.endTimer("healing search gpu")

		return exchangeData
	}

	@throttled(50)
	public run() {
		if (this.waitingSync) {
			return
		}

		this.waitingSync = true
		let result: ExchangeData | undefined = undefined

		const task = this.renderTasks.shift()
		if (task) {
			result = this.findSource_GPU(task.targetLocation, task.strokes)
		} else {
			// this.renderer.backend.gl.finish()
		}

		this.renderer.backend.waitClientSync(() => {
			const task = this.renderTasks.shift()
			if (task) {
				const result = this.findSource_GPU(task.targetLocation, task.strokes)
				this.renderer.backend.waitClientSync(() => (this.waitingSync = false))
				task.callback(result)
			} else {
				this.waitingSync = false
			}
		})

		task?.callback(result)
	}

	private runGpuTask = (
		targetLocation: Rectangle,
		strokes: BrushStroke[],
	): Promise<ExchangeData | undefined> => {
		return new Promise<ExchangeData | undefined>((resolve) => {
			const callback = (value: ExchangeData | undefined) => {
				resolve(value)
			}
			this.renderTasks.push({ targetLocation, strokes, callback })
		})
	}

	private runCpuTask = (
		targetLocation: Rectangle,
		exchangeData: ExchangeData,
	): Promise<Rectangle | undefined> => {
		return new Promise<Rectangle | undefined>((resolve) => {
			const loc = this.findOptimalSourceLoc_CPU(targetLocation, exchangeData)
			resolve(loc)
		})
	}

	detect = (
		targetLocation: Rectangle,
		strokes: BrushStroke[],
	): Promise<Rectangle | undefined> => {
		const rtn = new Promise<Rectangle | undefined>((resolve) => {
			this.runGpuTask(targetLocation, strokes).then((data) => {
				if (data) {
					const loc = this.runCpuTask(targetLocation, data)
					resolve(loc)
				} else {
					resolve(undefined)
				}
			})
		})

		return rtn
	}

	dispose() {
		this.texturesPool.dispose()
	}
}
